import type { RouteAccess } from "@carepass/auth";
import type { RouteDefinition } from "@carepass/routing";

export type MemberSurface = "public" | "auth" | "signup" | "partner-join" | "member" | "not-found";

export interface MemberRouteMetadata {
  readonly id: string;
  readonly surface: MemberSurface;
  readonly title: string;
  readonly description: string;
  readonly access: RouteAccess;
}

function route(path: string, metadata: MemberRouteMetadata): RouteDefinition<MemberRouteMetadata> {
  return { path, metadata };
}

const publicRoutes = [
  ["/", "PUB-S01", "공개 사이트", "서비스 소개, 이용 방법, 요금제와 고객센터를 제공합니다."],
  ["/service", "PUB-S02", "서비스 소개", "CarePass 서비스 범위와 이용 조건을 안내합니다."],
  ["/how-it-works", "PUB-S03", "이용 방법", "가입부터 거래 처리까지의 절차를 안내합니다."],
  ["/plans", "PUB-S04", "요금제", "월정액과 제공 범위를 비교합니다."],
  ["/benefits", "PUB-S05", "혜택지원", "혜택지원의 대상과 처리 절차를 안내합니다."],
  ["/notices", "PUB-S06", "공지사항", "게시 중인 공지사항을 확인합니다."],
  ["/faq", "PUB-S07", "자주 묻는 질문", "서비스 이용 중 자주 묻는 내용을 확인합니다."],
  ["/support", "PUB-S08", "1:1 문의", "로그인 후 1:1 문의로 이동합니다."],
] as const;

const authRoutes = [
  ["/login", "AUTH-S01", "회원 로그인", "CarePass 회원 서비스에 로그인합니다."],
  ["/login/password", "AUTH-S05", "비밀번호 입력", "확인된 회원이 숫자 6자리 비밀번호를 입력합니다."],
  ["/activate", "AUTH-S02", "최초 로그인", "가입과 최초 결제를 완료한 회원이 본인인증 후 비밀번호를 설정합니다."],
  ["/password-reset", "AUTH-S03", "비밀번호 재설정", "본인확인 후 로그인 비밀번호를 다시 설정합니다."],
  ["/payments/return", "PAY-S01", "결제 결과 확인", "서버에 저장된 결제 결과를 확인합니다."],
  ["/termination/result", "AUTH-S04", "해지 결과 확인", "본인확인 후 해지와 환불 상태를 확인합니다."],
] as const;

const authenticatedRoutes = [
  ["/app/transactions/:transactionId/payment", "MEM-S18", "카드 결제", "승인 금액을 결제하고 카드결제 결과를 확인합니다."],
  ["/app/transactions/:transactionId/transfer", "MEM-S20", "송금하기", "결제 완료된 거래의 즉시 또는 지정일 송금을 선택합니다."],
  ["/app/transactions/new", "MEM-S03", "거래 등록", "예금주 확인과 증빙 제출 후 거래를 등록합니다."],
  ["/app/transactions/:transactionId", "MEM-S04", "거래 상세", "심사, 결제, 송금과 문서 상태를 확인합니다."],
  ["/app/transactions", "MEM-S02", "거래 목록", "거래 상태와 처리 이력을 확인합니다."],
  ["/app/benefits/new", "MEM-S06", "혜택지원 신청", "피해정보와 증빙을 제출합니다."],
  ["/app/benefits/:claimId", "MEM-S16", "혜택지원 상세", "신청과 승인·지급 상태를 확인합니다."],
  ["/app/benefits", "MEM-S05", "혜택지원", "혜택지원 신청과 처리 결과를 확인합니다."],
  ["/app/notifications", "MEM-S07", "알림", "처리 결과와 필요한 다음 행동을 확인합니다."],
  ["/app/inquiries/new", "MEM-S09", "문의 접수", "첨부자료와 함께 문의를 접수합니다."],
  ["/app/inquiries/:inquiryId", "MEM-S17", "문의 상세", "문의 내용과 답변을 확인합니다."],
  ["/app/inquiries", "MEM-S08", "문의", "접수한 문의와 답변을 확인합니다."],
  ["/app/my", "MEM-S19", "MY", "회원정보, 구독과 고객지원 메뉴를 확인합니다."],
  ["/app/my/subscription", "MEM-S10", "구독 관리", "현재 요금제와 변경 가능 여부를 확인합니다."],
  ["/app/my/billing", "MEM-S11", "월정액 내역", "월정액 결제와 환불 상태를 확인합니다."],
  ["/app/my/sessions", "MEM-S12", "로그인 기기", "로그인된 기기를 조회하고 해제합니다."],
  ["/app/my/profile", "MEM-S13", "MY", "본인의 정보를 확인하고 이메일을 변경합니다."],
  ["/app/my/password", "MEM-S21", "비밀번호 재설정", "기존 비밀번호 확인 후 새 비밀번호를 설정합니다."],
  ["/app/my/terms", "MEM-S14", "약관과 동의", "약관 게시 버전과 동의 시점을 관리합니다."],
  ["/app/my/termination", "MEM-S15", "서비스 해지", "진행 거래와 해지·환불 상태를 확인합니다."],
  ["/app", "MEM-S01", "회원 홈", "구독, 한도, 거래와 알림의 현재 상태를 확인합니다."],
] as const;

export const memberRoutes: readonly RouteDefinition<MemberRouteMetadata>[] = [
  route("/signup", {
    id: "ACQ-S02",
    surface: "signup",
    title: "일반 가입",
    description: "약관 동의, 본인확인과 최초 결제를 거쳐 회원가입을 완료합니다.",
    access: "public",
  }),
  route("/join", {
    id: "ACQ-S01-V04",
    surface: "partner-join",
    title: "제휴 가입창",
    description: "제휴 가입 결과를 전달한 뒤 원래 결제창으로 복귀합니다.",
    access: "partner",
  }),
  route("/partner/join", {
    id: "ACQ-S01",
    surface: "partner-join",
    title: "제휴 가입창",
    description: "제휴 광고 유입부터 가입 완료와 원 결제창 복귀까지 독립적으로 처리합니다.",
    access: "partner",
  }),
  route("/notices/:contentId", {
    id: "PUB-S06-DETAIL",
    surface: "public",
    title: "공지사항 상세",
    description: "게시 중인 공지사항 내용을 제공합니다.",
    access: "public",
  }),
  route("/terms/:termVersionId", {
    id: "PUB-S09",
    surface: "public",
    title: "약관 상세",
    description: "게시된 약관의 내용과 버전을 확인합니다.",
    access: "public",
  }),
  route("/terms", {
    id: "PUB-S09-LATEST", surface: "public", title: "약관 및 정책",
    description: "최신 게시 약관을 확인합니다.", access: "public",
  }),
  ...publicRoutes.map(([path, id, title, description]) => route(path, {
    id,
    surface: "public",
    title,
    description,
    access: "public",
  })),
  ...authRoutes.map(([path, id, title, description]) => route(path, {
    id,
    surface: "auth",
    title,
    description,
    access: "public",
  })),
  ...authenticatedRoutes.map(([path, id, title, description]) => route(path, {
    id,
    surface: "member",
    title,
    description,
    access: "member",
  })),
];

export const memberNotFoundRoute: RouteDefinition<MemberRouteMetadata> = route("*", {
  id: "MEMBER-NOT-FOUND",
  surface: "not-found",
  title: "페이지를 찾을 수 없습니다",
  description: "주소를 확인하거나 이전 화면으로 돌아가 주세요.",
  access: "public",
});
